import { FEATURES, GROUP_TYPE } from "@home-health-notify/domain";
import DeveloperModeIcon from "@mui/icons-material/DeveloperMode";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import LabelIcon from "@mui/icons-material/Label";
import LiveHelpIcon from "@mui/icons-material/LiveHelp";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { FormControlLabel, Switch } from "@mui/material";
import {
  DeveloperSettingsContext,
  OrganizationContext,
  useTranslation,
} from "hhn-commons";
import { useContext } from "react";
import { Link } from "react-router";
import CommonDrawerHeader from "../../common/CommonDrawerHeader";
import { RightDrawerContext } from "../../context/RightDrawerContext";
import { MODAL_ROUTES } from "../../routes";
import { DoNotDisturb } from "./DoNotDisturb";
import { MuteNotifications } from "./MuteNotifications";
import NotificationSettings from "./notificationSettings/NotificationSettings";
import {
  SettingsItem,
  SettingsItemLabel,
  SettingsItemsWrapper,
  SettingsWrapper,
  SwitchWrapper,
  VersionNumber,
} from "./UserSettings.style";

const UserSettings = ({
  chatId,
  groupType,
}: {
  chatId?: string;
  groupType?: GROUP_TYPE;
}) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "settings" });
  const { closeDrawer, openChatInfoDrawer } = useContext(RightDrawerContext);
  const { developerModeEnabled, setDeveloperModeEnabled } = useContext(
    DeveloperSettingsContext,
  );
  const { selectedOrgUser } = useContext(OrganizationContext);
  const canAddUser = !!selectedOrgUser?.hasAccess(FEATURES.ADD_USER);
  const canAddPatient = !!selectedOrgUser?.hasAccess(FEATURES.ADD_PATIENT);

  const openHelpLink = () => {
    const url = "https://qa.homehealthnotify.com/help"; //TODO: Change link if needed while implementing UserSettings feature
    window.open(url, "_blank");
  };

  const openAboutUsLink = () => {
    const url = "https://qa.homehealthnotify.com/about-us"; //TODO: Change link if needed while implementing UserSettings feature
    window.open(url, "_blank");
  };

  const openGroupSettings = () => {
    const isPatientGroup = groupType === GROUP_TYPE.PATIENT;
    if (chatId && groupType) openChatInfoDrawer(isPatientGroup ? 4 : 2);
  };

  const showBackButton = !!chatId;

  return (
    <SettingsWrapper>
      <CommonDrawerHeader
        showBackButton={showBackButton}
        onBackButtonClick={openGroupSettings}
        showCloseButton={!showBackButton}
        title={tNs("userSettings.settings")}
        onCloseButtonClick={closeDrawer}
      />
      <SettingsItemsWrapper data-testid="notifications-options">
        <SwitchWrapper>
          <DoNotDisturb />
        </SwitchWrapper>
        <SwitchWrapper>
          <MuteNotifications />
        </SwitchWrapper>
        <NotificationSettings />
        {canAddUser && (
          <Link
            to={`?modal=${MODAL_ROUTES.ADD_USER}`}
            data-testid="add-user-settings-item"
          >
            <SettingsItem>
              <PersonAddIcon color="secondary" />
              <SettingsItemLabel>
                {tNs("userSettings.addUser")}
              </SettingsItemLabel>
            </SettingsItem>
          </Link>
        )}
        {canAddPatient && (
          <Link
            to={`?modal=${MODAL_ROUTES.ADD_PATIENT}`}
            data-testid="add-patient-settings-item"
          >
            <SettingsItem>
              <GroupAddIcon color="secondary" />
              <SettingsItemLabel>
                {tNs("userSettings.addPatient")}
              </SettingsItemLabel>
            </SettingsItem>
          </Link>
        )}
        <SettingsItem data-testid="help-settings-item" onClick={openHelpLink}>
          <LiveHelpIcon color="secondary" />
          <SettingsItemLabel>{tNs("userSettings.help")}</SettingsItemLabel>
        </SettingsItem>
        <SettingsItem
          data-testid="about-us-settings-item"
          onClick={openAboutUsLink}
        >
          <LabelIcon color="secondary" />
          <SettingsItemLabel>{tNs("userSettings.aboutUs")}</SettingsItemLabel>
        </SettingsItem>
        <SwitchWrapper>
          <DeveloperModeIcon color="secondary" sx={{ marginRight: 2 }} />
          <FormControlLabel
            control={
              <Switch
                checked={developerModeEnabled}
                onChange={e => setDeveloperModeEnabled(e.target.checked)}
              />
            }
            label="Developer Mode"
            labelPlacement="start"
            sx={{ flex: 1, margin: 0, justifyContent: "space-between" }}
          />
        </SwitchWrapper>
        <VersionNumber>Version: 2.34.11</VersionNumber>
      </SettingsItemsWrapper>
    </SettingsWrapper>
  );
};

export default UserSettings;
